// src/services/alerts/index.js (UPDATED)
// REPLACED: direct proxyFetch() calls to NWS/USGS.
// NOW: reads from Firestore public_snapshots/ written by Cloud Functions.
//
// Upstream API call count is now independent of user count.
// 1 user or 200,000 users = same upstream load.

import {
  readNWSAlerts, readUSGSGauges, isSnapshotFresh, snapshotAgeMs,
} from '@services/cache/serverCache';

// ── NWS Alerts ────────────────────────────────────────────────────────────
// Server polls every 3 min → writes to public_snapshots/nws_alerts
// Clients read that one document — no direct NWS API calls.
export async function fetchNWSAlerts({ useCache = true } = {}) {
  const snapshot = await readNWSAlerts();

  if (!snapshot) {
    // No server data yet — return empty with explicit unavailable signal
    return { alerts: [], sourceState: 'UNAVAILABLE', fetchedAt: null, stale: true };
  }

  const fresh = isSnapshotFresh(snapshot);
  const ageMs = snapshotAgeMs(snapshot);

  return {
    alerts:      Array.isArray(snapshot.data?.features) ? snapshot.data.features : [],
    sourceState: fresh ? 'LIVE' : 'STALE',
    fetchedAt:   snapshot.fetchedAt,
    expiresAt:   snapshot.expiresAt,
    stale:       !fresh,
    ageMs,
    fromIDBFallback: snapshot.fromIDBFallback ?? false,
  };
}

// SWR pattern: return cached immediately, refresh in background
export async function fetchNWSAlertsSWR(onFresh) {
  const cached = await fetchNWSAlerts({ useCache: true });
  if (cached.alerts.length > 0) onFresh?.(cached);
  // Firestore real-time listener handles updates — no polling needed here
  return cached;
}

// ── USGS Gauges ───────────────────────────────────────────────────────────
// Server polls every 3 min → writes to public_snapshots/usgs_gauges
export async function fetchUSGSGauges({ useCache = true } = {}) {
  const snapshot = await readUSGSGauges();

  if (!snapshot) {
    return { gauges: [], sourceState: 'UNAVAILABLE', stale: true };
  }

  const fresh = isSnapshotFresh(snapshot);

  const _gaugeData = snapshot.data?.gauges;
  return {
    gauges:      Array.isArray(_gaugeData) ? _gaugeData : [],
    sourceState: fresh ? 'LIVE' : 'STALE',
    fetchedAt:   snapshot.fetchedAt,
    stale:       !fresh,
    ageMs:       snapshotAgeMs(snapshot),
    fromIDBFallback: snapshot.fromIDBFallback ?? false,
  };
}

// ── NOAA Surge ────────────────────────────────────────────────────────────
export async function fetchNOAASurge() {
  const { readNOAASurge } = await import('@services/cache/serverCache');
  const snapshot = await readNOAASurge();
  if (!snapshot) return { stations: [], sourceState: 'UNAVAILABLE', stale: true };
  return {
    stations:    snapshot.data?.stations ?? [],
    sourceState: isSnapshotFresh(snapshot) ? 'LIVE' : 'STALE',
    fetchedAt:   snapshot.fetchedAt,
    stale:       !isSnapshotFresh(snapshot),
  };
}

// ── proxyFetch — legacy export for src/services/weather/index.js ──────────
// Kept for backward compatibility. Prefer readSnapshot() for cached sources.
const _PROXY = import.meta.env.VITE_CORS_PROXY ?? '';
export async function proxyFetch(url, timeoutMs = 8000) {
  const target = _PROXY ? _PROXY + encodeURIComponent(url) : url;
  const res = await fetch(target, { signal: AbortSignal.timeout(timeoutMs) });
  if (res.status >= 400) throw new Error('proxyFetch ' + res.status + ': ' + url);
  return res;
}

// ── fetch511LA — reads DOTD traffic from server cache ─────────────────────
export async function fetch511LA() {
  const { readDOTDTraffic } = await import('@services/cache/serverCache');
  const snapshot = await readDOTDTraffic();
  if (!snapshot) return { incidents: [], sourceState: 'UNAVAILABLE', stale: true };
  return {
    incidents:   snapshot.data?.closures ?? [],
    evacMessages:snapshot.data?.evacMessages ?? [],
    sourceState: snapshot.stale ? 'STALE' : 'LIVE',
    fetchedAt:   snapshot.fetchedAt,
    stale:       snapshot.stale ?? false,
  };
}
